/**
 * Data-Fix finding grammar (§B3) — the FE side of the pinned Custodian
 * contract. Each of L's 13 rule classes folds into a display GROUP (badge +
 * filter chip) and an ACTION KIND that drives one of J's existing callable
 * affordances. The server-authored `body` is always the finding sentence, and
 * evidence rows are composed from the fields `meta` actually carries — so a
 * class we don't yet fully model still reads correctly and stays resolvable.
 * Richer per-class evidence + the duplicate suspect signal slot in when L
 * confirms the exact meta shapes; the layout holds either way.
 */

import { formatDate, formatMoney } from "../../data/format";

// The 13 rule classes → E's display groups (§B2). name_format / note_noise are
// data-quality nits with no group of their own in E's five, so they get one.
export const RULE_GROUPS = {
  duplicate_active: "Duplicate policies",
  duplicate_policy_number: "Duplicate policies",
  implausible_premium: "Premiums",
  expired_active: "Dates",
  missing_expiration: "Dates",
  implausible_date: "Dates",
  converted_unlinked: "Unlinked",
  orphan_quote: "Unlinked",
  orphan_document: "Unlinked",
  quote_prospect_link: "Unlinked",
  imported_triage_sold: "Staging",
  imported_triage_retention: "Staging",
  name_format: "Data quality",
  note_noise: "Data quality"
};

export const GROUP_ORDER = [
  "Duplicate policies",
  "Premiums",
  "Dates",
  "Unlinked",
  "Staging",
  "Data quality"
];

export function groupForRule(rule) {
  return RULE_GROUPS[rule] || "Data quality";
}

// Which affordance the primary button drives:
//   policy → PolicyStatusAction (mark a policy inactive)
//   link   → LinkClientAction (attach a prospect to its existing client)
//   record → deep-link to the record; the fix happens in the normal drawer
//   review → open-and-review only (no confident single action)
const ACTION_KIND = {
  expired_active: "policy",
  duplicate_active: "policy",
  duplicate_policy_number: "policy",
  converted_unlinked: "link",
  imported_triage_sold: "link",
  quote_prospect_link: "quote-link", // L T4 backfill — Coordinator-confirmed: ATTRIBUTE the quote to its matched prospect (quote-update {prospectRef}), NOT LinkClientAction
  implausible_premium: "record",
  implausible_date: "record",
  missing_expiration: "record",
  name_format: "record",
  imported_triage_retention: "record",
  orphan_quote: "review",
  orphan_document: "review",
  note_noise: "review"
};

export function actionKindFor(rule) {
  return ACTION_KIND[rule] || "review";
}

const MODEL_LOB = { Auto: "auto", Property: "home", Umbrella: "umbrella" };
export function lobKeyForModel(model) {
  return MODEL_LOB[model] || null;
}

/**
 * The suspect policy for a policy-status finding, as a shape PolicyStatusAction
 * accepts ({_id, lobKey, policyActive}). Confident only when meta names a single
 * target: meta.suspectRef, or exactly one member. Two-plus members with no
 * suspect signal → null (the card falls to "Ask the Fawn", never a wrong flip).
 */
export function suspectPolicy(finding) {
  const meta = finding.meta || {};
  const members = Array.isArray(meta.members) ? meta.members : [];
  let target = null;
  if (meta.suspectRef) {
    target = members.find((m) => String(m._id) === String(meta.suspectRef)) || null;
  } else if (members.length === 1) {
    target = members[0];
  }
  if (!target) {
    return null;
  }
  const lobKey = lobKeyForModel(target.model);
  if (!lobKey) {
    return null; // no inactivate route for this line (e.g. OtherPolicy)
  }
  // Name the exact change on the button (§B3): "Mark Travelers auto inactive".
  const label = target.carrier ? `Mark ${target.carrier} ${lobKey} inactive` : "Mark inactive";
  return { _id: target._id, lobKey, policyActive: true, label };
}

/**
 * The prospect a link-kind finding attaches, as a shape LinkClientAction accepts.
 * Two carriers (confirmed from L's lib/custodian.js): the subject IS the prospect
 * (converted_unlinked / imported_triage_sold), OR the subject is a Quote and the
 * matched prospect rides in meta.candidates[0] as {kind:"prospect", _id, name}
 * (quote_prospect_link's T4 backfill).
 */
export function subjectProspect(finding) {
  if (finding.subjectModel === "Prospect" && finding.subjectRef) {
    return { _id: finding.subjectRef, name: finding.subjectName || "this prospect" };
  }
  const candidates =
    finding.meta && Array.isArray(finding.meta.candidates) ? finding.meta.candidates : [];
  const cand = candidates.find((c) => c && c.kind === "prospect" && c._id);
  if (cand) {
    return { _id: cand._id, name: cand.name || "the matched prospect" };
  }
  return null;
}

// Friendly "why" for L's deterministic suspect reasons (duplicate classes).
const SUSPECT_WHY = {
  "expired-term": "term expired",
  "older-term": "older term — superseded by renewal"
};

/**
 * Evidence rows from L's pinned per-class meta dictionary: the policy-row grammar
 * (carrier · #number · term · premium), the ⚠ suspect marked from meta.suspectRef
 * with its "why" subline from meta.suspectReason, and candidate matches. Every
 * field is optional — a class that carries less still reads correctly.
 */
export function evidenceRows(finding) {
  const meta = finding.meta || {};
  const members = Array.isArray(meta.members) ? meta.members : [];
  const rows = members.map((m, i) => {
    const isSuspect = meta.suspectRef
      ? String(m._id) === String(meta.suspectRef)
      : members.length === 1;
    const parts = [
      [m.carrier, m.policyNumber ? `#${m.policyNumber}` : null].filter(Boolean).join(" · ") ||
        m.model ||
        "Policy"
    ];
    if (m.effectiveDate) {
      parts.push(
        m.expirationDate
          ? `eff ${formatDate(m.effectiveDate)} → ${formatDate(m.expirationDate)}`
          : `eff ${formatDate(m.effectiveDate)}`
      );
    } else if (m.expirationDate) {
      parts.push(`exp ${formatDate(m.expirationDate)}`);
    }
    // implausible_premium carries the flagged premium on meta (a single-member class).
    if (members.length === 1 && meta.premium != null) {
      parts.push(`${formatMoney(meta.premium)}/yr`);
    }
    return {
      key: m._id || `m${i}`,
      label: parts.join(" · "),
      suspect: isSuspect,
      why:
        isSuspect && meta.suspectReason
          ? SUSPECT_WHY[meta.suspectReason] || meta.suspectReason
          : null
    };
  });
  const candidates = Array.isArray(meta.candidates) ? meta.candidates : [];
  candidates.forEach((c, i) => {
    rows.push({
      key: `c${c._id || i}`,
      label: `Possible match: ${c.name || c.email || c._id}`,
      suspect: false,
      why: null
    });
  });
  return rows;
}

/**
 * The hedged proposal (§B0 law 1: findings state facts, proposals hedge). Each
 * returns { text, actionLabel } — actionLabel is the bolded change and the
 * confident primary button's label; ambiguous classes carry a null actionLabel.
 */
const PROPOSALS = {
  expired_active: {
    text: "the term has expired with no recent activity",
    actionLabel: "Mark inactive"
  },
  duplicate_active: { text: "the older policy looks superseded", actionLabel: "Mark inactive" },
  duplicate_policy_number: {
    text: "two policies share a number — one looks stale",
    actionLabel: "Mark inactive"
  },
  converted_unlinked: {
    text: "this prospect appears to have become an existing client",
    actionLabel: "Link to household"
  },
  imported_triage_sold: {
    text: "this imported prospect looks like a client already on the book",
    actionLabel: "Link to household"
  },
  quote_prospect_link: {
    text: "this quote matches a prospect already on the book",
    actionLabel: "attribute it to that prospect"
  },
  implausible_premium: { text: "the premium on file looks off", actionLabel: null },
  implausible_date: { text: "a date on file looks off", actionLabel: null },
  missing_expiration: { text: "no expiration date is on file", actionLabel: null },
  name_format: { text: "the name formatting looks off", actionLabel: null },
  imported_triage_retention: {
    text: "this imported prospect looks like a re-quote for an existing client",
    actionLabel: null
  },
  orphan_quote: { text: "this quote isn't linked to a client", actionLabel: null },
  orphan_document: { text: "this document isn't linked to a record", actionLabel: null },
  note_noise: { text: "this note looks like low-signal noise", actionLabel: null }
};

export function proposalFor(rule) {
  return PROPOSALS[rule] || { text: "the Custodian flagged this for a look", actionLabel: null };
}

/** E's four dismiss reasons (§B4). The note is composed as "<slug>: <text>". */
export const DISMISS_REASONS = [
  { slug: "not_a_problem", label: "Not a problem" },
  { slug: "already_handled", label: "Already handled" },
  { slug: "legitimate_overlap", label: "Legitimate overlap" },
  { slug: "something_else", label: "Something else…" }
];
